// @ts-strict-ignore
import { v4 as uuidv4 } from 'uuid';
import { ActionType, AggregationType } from '../constants';
import type {
  Action,
  AggregationComponent,
  Attribute,
  BooleanRule,
  IndexedRule,
  SchemaInstance,
  WorkflowOperationMessage,
  WorkflowProps,
  ReasonCodeGroup,
  AttributeComponent,
} from '../types';
import type { AggregationConfig, AggregationConfigByObjectType, AggregationObjectType } from '~/dashboard/views/settings/aggregations/types';
import {
  OperationMessageType,
} from '../types';
import { isActionOfType, getAllowedAttributesWithMatchingDataType } from '../../../service/actions';
import { BEST_EFFORT_PARSING_DEFAULT_STRING, supportedNullActionTypes } from 'flow/service/thresholds';
import type { WorkflowState } from 'flow/store/redux/state';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import { transformMLAAttributeComponents } from '../transformation/thresholds';
import { getActiveAttributes, sortAttributes } from 'flow/service/actions/services/services';

dayjs.extend(utc);

const AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME: Record<
string,
AggregationObjectType | undefined> = {
  entities: 'entity',
};

export function getDefaultAction(args: {
  sourceAction: Action;
  workflowData: Partial<SchemaInstance>;
}) {
  const { sourceAction, workflowData } = args;

  const firstDependency = workflowData.dependencies[sourceAction.publicId][0];
  const firstAction = workflowData.actions[firstDependency.id];

  return firstAction;
}

export function assignDefaultsToMLAFilterComponents({
  mlaFilterComponents,
}: {
  mlaFilterComponents: AttributeComponent[];
}) {
  return mlaFilterComponents.map(({ attributeComponent, value }) => {
    let updatedDefaultValue;
    if (!value && attributeComponent.default && !attributeComponent.isOptional) {
      if (attributeComponent.dataType === 'csv') {
        updatedDefaultValue = attributeComponent.default;
      } else if (attributeComponent.dataType === 'multi_select') {
        updatedDefaultValue = [attributeComponent.default];
      } else {
        updatedDefaultValue = attributeComponent.default;
      }
    } else {
      updatedDefaultValue = undefined;
    }

    return {
      value: updatedDefaultValue,
      attributeComponent,
    };
  });
}

export function getDefaultAttribute(args: {
  action: Action;
  workflowProps: WorkflowProps;
}): Attribute {
  const { action, workflowProps } = args;

  if (!isActionOfType(action, ActionType.SERVICE)) {
    return null;
  }

  const service = workflowProps.services[action.service.internalName];

  const activeAttributes = getActiveAttributes(service.attributes);
  const allowedAttributes: {
    [attribute_id: number]: Attribute;
  } = getAllowedAttributesForPortfolioWorkflow(
    workflowProps.workflow.type,
    service.name,
    activeAttributes,
  );
  const sortedAttributes = sortAttributes(allowedAttributes);

  return sortedAttributes[0];
}

export function getAttribute(
  action: Action,
  workflowProps: WorkflowProps,
  opts: { attributeId?: number; dataType?: string },
): Attribute | null {
  const { attributeId, dataType } = opts;

  if (!isActionOfType(action, ActionType.SERVICE)) {
    return null;
  }

  const service = workflowProps.services[action.service.internalName];

  // Specific attribute was requested
  if (attributeId !== undefined) {
    const { attributes } = service;
    return attributes[attributeId];
  }

  // Find a data-type compatible attribute
  if (dataType) {
    const compatibleAttributes = getAllowedAttributesWithMatchingDataType({
      service,
      incomingDataType: dataType,
      workflowType: workflowProps.workflow.type,
    });
    const sortedAttributes = sortAttributes(compatibleAttributes);
    return sortedAttributes[0] || null;
  }

  // Otherwise use the default attribute
  return getDefaultAttribute({ action, workflowProps });
}

/* assumes that the attribute is a mla */
export function getDefaultMLAAttributeComponents(attribute: Attribute) {
  if (!attribute.components) {
    return [];
  }

  const transformedComponents = transformMLAAttributeComponents({
    attributeFilterComponents: attribute.components,
  });

  return assignDefaultsToMLAFilterComponents({
    mlaFilterComponents: transformedComponents,
  });
}

type AggregationAttributeInput = Pick<Attribute, 'attribute_name' | 'display_name'>;

/**
 * Returns the aggregation config for an attribute, or undefined if the attribute
 * has no mapped object type.
 */
export function getAggregationConfigForAttribute(
  attribute: AggregationAttributeInput,
  aggregationConfig: AggregationConfigByObjectType,
): AggregationConfig | undefined {
  const objectType = AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME[attribute.attribute_name];
  return objectType ? aggregationConfig[objectType] : undefined;
}

/**
 * Returns existing aggregation components if present, otherwise builds defaults from the attribute
 * and config. Looks up config by object type internally (single lookup).
 */
export function resolveAggregationComponents(
  attribute: AggregationAttributeInput,
  aggregationConfig: AggregationConfigByObjectType,
  existing?: AggregationComponent[],
): AggregationComponent[] {
  if (existing?.length) {
    return existing;
  }

  const config = getAggregationConfigForAttribute(attribute, aggregationConfig);
  if (!config) return [];

  return [{
    displayName: attribute.display_name ?? attribute.attribute_name,
    displayDescription: '',
    objectSlug: AGGREGATION_OBJECT_TYPE_BY_ATTRIBUTE_NAME[attribute.attribute_name]
      ?? attribute.attribute_name,
    objectVersionPublicVersion: config.publicVersion,
    aggregation: {
      aggregationType: AggregationType.COUNT_DISTINCT,
      columnSlug: 'entity_token',
    },
    filters: [],
  }];
}

export function getDefaultReasonCodeGroup(reasonCodeGroups: ReasonCodeGroup[]) {
  if (!reasonCodeGroups || !reasonCodeGroups.length) {
    return null;
  }

  const sortedReasonCodeGroups = [...reasonCodeGroups].sort(
    (a, b) => a.name.localeCompare(b.name),
  );

  return sortedReasonCodeGroups[0];
}

export function getDefaultRightAttributeValue(
  dataType: string | null, // tags do not have a data type
  supportsNull: boolean,
  leftAttribute?: Attribute,
) {
  // Skip returning default value for reasoncodes data type
  if (dataType !== 'reasoncodes'
    && leftAttribute?.default
    && (!Array.isArray(leftAttribute.default) || leftAttribute.default.length > 0)) {
    const attributeDefaultValue = (
      Array.isArray(leftAttribute.default)
        ? leftAttribute.default[0]
        : leftAttribute.default
    );

    return attributeDefaultValue;
  }

  // the preset value on reasoncode attrs should not be used
  if (dataType !== 'reasoncodes' && leftAttribute?.values) {
    return leftAttribute.values;
  }

  switch (dataType) {
    case 'boolean': {
      return 'true';
    }
    case 'string': {
      return supportsNull ? null : '';
    }
    case 'integer': {
      return 0;
    }
    case 'decimal': {
      return 0.0;
    }
    case 'timestamp': {
      return dayjs.utc().format();
    }
    case 'date': {
      return dayjs.utc().format('YYYY-MM-DD');
    }
    case 'reasoncodes':
    default: {
      return null;
    }
  }
}

export function getOperatorUsedInRuleObject({
  rule,
}: {
  rule: IndexedRule | BooleanRule;
}) {
  if (Array.isArray(rule)) return false;
  if ('id' in rule) return false;
  if ('and' in rule) {
    return 'and';
  }
  if ('or' in rule) {
    return 'or';
  }

  return false;
}

export function getOperatorAndRightAttrValues({
  leftAttribute,
  leftAttributeDataType,
  leftAttributeValues,
  leftAction,
  reasonCodeGroups,
  featureFlags,
}: {
  leftAttribute: Attribute;
  leftAttributeDataType: string;
  leftAttributeValues: any[];
  leftAction: Action;
  reasonCodeGroups: ReasonCodeGroup[];
  featureFlags: WorkflowState['featureFlagsForStore'];
}) {
  const thresholdRightSide = {
    actionId: null,
    attributeId: null,
    attributeToken: null,
    attributeComponents: [],
    reasonCodeGroupId: null,
    reasonCodeGroup: null,
    attributeValue: null,
    leftTagActionResult: null,
    aggregationComponents: [],
  };

  let rightAttrValue;
  switch (leftAttributeDataType) {
    case 'string': {
      if (featureFlags.allowBestEffortRuleEvaluations
        && supportedNullActionTypes.has(leftAction.type)
      ) {
        rightAttrValue = BEST_EFFORT_PARSING_DEFAULT_STRING;
      } else if (isActionOfType(leftAction, [
        ActionType.INPUT, ActionType.OUTPUT,
        ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
        ActionType.IMPORTED_OUTPUT_ATTRIBUTE, ActionType.SERVICE,
      ])) {
        rightAttrValue = '';
      } else if (leftAttribute.default) {
        const attributeDefaultValue = Array.isArray(leftAttribute.default)
          ? leftAttribute.default[0]
          : leftAttribute.default;
        const matchingDefaultValue = leftAttributeValues
          ? leftAttributeValues.find(
            attrValue => attrValue === attributeDefaultValue)
          : attributeDefaultValue;
        if (matchingDefaultValue) {
          rightAttrValue = matchingDefaultValue;
        } else {
          rightAttrValue = '';
        }
      } else {
        rightAttrValue = '';
      }

      return {
        operator: 'e',
        right: {
          ...thresholdRightSide,
          attributeValue: rightAttrValue,
        },
      };
    }
    case 'decimal':
    case 'integer': {
      rightAttrValue = leftAttributeDataType === 'integer'
        ? '0'
        : '0.0';
      if (leftAttribute?.default) {
        const attributeDefaultValue = Array.isArray(leftAttribute.default)
          ? leftAttribute.default[0]
          : leftAttribute.default;
        rightAttrValue = attributeDefaultValue || '';
      } else if (leftAttributeValues && leftAttributeValues.length > 1) {
        [rightAttrValue] = leftAttributeValues;
      }
      return {
        operator: 'lt',
        right: {
          ...thresholdRightSide,
          attributeValue: rightAttrValue,
        },
      };
    }
    case 'boolean': {
      return {
        operator: 'e',
        right: {
          ...thresholdRightSide,
          attributeValue: true,
        },
      };
    }
    case 'date':
    case 'timestamp': {
      return {
        operator: 'lt',
        right: {
          ...thresholdRightSide,
          attributeValue: leftAttribute && leftAttribute.default
            ? leftAttribute.default
            : dayjs.utc().format('YYYY-MM-DD'),
        },
      };
    }
    case 'reasoncodes': {
      // Get alphabetically-first reason code group
      const sortedReasonCodeGroups = [...reasonCodeGroups]
        .sort((a, b) => a.name.localeCompare(b.name));
      return {
        operator: 'in',
        right: {
          ...thresholdRightSide,
          ...(sortedReasonCodeGroups[0]?.id ? {
            reasonCodeGroupId: sortedReasonCodeGroups[0].id,
            reasonCodeGroup: sortedReasonCodeGroups[0],
          } : {}),
        },
      };
    }
  }

  return {
    operator: '',
    right: thresholdRightSide,
  };
}

export const buildActionRuleThresholdOperationMsg = ({
  publicId,
  message,
  data,
}: {
  publicId: string;
  message: WorkflowOperationMessage['message'];
  data: any;
}) => ({
  id: uuidv4(),
  publicId,
  type: OperationMessageType.actionRuleThreshold,
  timestamp: Date.now(),
  message,
  data,
});
